import { useMemo, useCallback } from 'react';
import { useAppThunkDispatch } from 'state/store';
import { useSelector } from 'react-redux';
import {
  getBusinessTeamsList,
  getSelectedBusinessTeamsList,
  selectAllBusinessTeams,
  selectCanFetchMoreBusinessTeams,
  selectSelectedBusinessTeamIds,
  selectSelectedBusinessTeams,
} from 'state/ProjectsList/filters/Teams/businessTeamsSlice';
import { BusinessUnitSummary } from 'types/store/normalized/businessUnit';

interface UseProjectBusinessUnitsParams {
  appliedFilters?: string[];
}

interface UseProjectBusinessUnitsReturn {
  fetchBusinessTeams: () => Promise<void>;
  businessTeams: BusinessUnitSummary[];
  canFetchMoreBusinessTeams: boolean;
  selectedBusinessTeamIds: string[];
  selectedBusinessTeams: BusinessUnitSummary[];
  shouldFetchBusinessTeams: boolean;
  fetchSelectedBusinessTeams: (selected: string[]) => Promise<void>;
}

export const useProjectBusinessUnits = ({
  appliedFilters,
}: UseProjectBusinessUnitsParams): UseProjectBusinessUnitsReturn => {
  const dispatch = useAppThunkDispatch();
  const businessTeams = useSelector(selectAllBusinessTeams);
  const selectedBusinessTeamIds = useSelector(selectSelectedBusinessTeamIds);
  const selectedBusinessTeams = useSelector(selectSelectedBusinessTeams);
  const canFetchMoreBusinessTeams = useSelector(
    selectCanFetchMoreBusinessTeams
  );

  const fetchBusinessTeams = useCallback(async () => {
    await dispatch(getBusinessTeamsList());
  }, [dispatch]);

  const fetchSelectedBusinessTeams = useCallback(
    async (selected: string[]) => {
      await dispatch(
        getSelectedBusinessTeamsList({ selectedBusinessTeamIds: selected })
      );
    },
    [dispatch]
  );

  const shouldFetchBusinessTeams = useMemo(() => {
    if (appliedFilters && appliedFilters.length > 0) {
      return (
        selectedBusinessTeams.length === 0 ||
        appliedFilters.length !== selectedBusinessTeamIds.length
      );
    }
    return businessTeams.length === 0;
  }, [
    appliedFilters,
    businessTeams.length,
    selectedBusinessTeamIds.length,
    selectedBusinessTeams.length,
  ]);

  return useMemo(
    () => ({
      fetchBusinessTeams,
      businessTeams,
      selectedBusinessTeamIds,
      selectedBusinessTeams,
      canFetchMoreBusinessTeams,
      shouldFetchBusinessTeams,
      fetchSelectedBusinessTeams,
    }),
    [
      fetchBusinessTeams,
      businessTeams,
      selectedBusinessTeamIds,
      selectedBusinessTeams,
      canFetchMoreBusinessTeams,
      shouldFetchBusinessTeams,
      fetchSelectedBusinessTeams,
    ]
  );
};
